import { UserRoleType, UserStatus } from '../../../enums/enums';
import { type User } from '../../../models/CoachingSessionFullDTO';
import { type IUserCareerGoal, type IUserProfile } from '../../../models/userProfile';
import { getDateOffset } from '../support/date-utils';
import { devBypassAuthUser } from './auth';

export const latestCareerGoals: IUserCareerGoal[] = [
  {
    date: getDateOffset(-14, 9),
    goal: 'Develop a stronger executive presence and become more effective at leading cross-functional teams.'
  }
];

// WHY: Jordan is the executive persona used to demonstrate active coaching, PowerLab participation,
// and a realistic multi-program progress story.
export const jordanCareerGoals: IUserCareerGoal[] = [
  {
    date: getDateOffset(-30, 9),
    goal: 'Create clarity and alignment for senior leaders navigating AI-enabled change.'
  }
];

// WHY: Evan is the CEO persona used to demonstrate a senior executive with two distinct 1:1
// coaching tracks: AI transformation and broader executive leadership.
export const ceoCareerGoals: IUserCareerGoal[] = [
  {
    date: getDateOffset(-24, 9),
    goal: 'Scale a durable operating rhythm while leading responsible AI adoption across the company.'
  }
];

export function buildSessionUser(overrides: Partial<User>): User {
  return {
    id: 'user-id',
    name: 'BestHuman User',
    email: 'user@besthuman.test',
    email2: '',
    type: UserRoleType.Mentee,
    clientId: 'client-dev-1',
    avatar: '',
    createdAt: getDateOffset(-180, 8),
    updatedAt: getDateOffset(-1, 8),
    isCoach: false,
    isCoachee: true,
    linkedin: '',
    title: 'Director of Product',
    company: 'Northwind Labs',
    age: '',
    experience: '',
    city: 'Vancouver',
    country: 'Canada',
    timezone: 'America/Vancouver',
    status: UserStatus.Active,
    note: '',
    isCertifiedCoach: false,
    isFacilitator: false,
    careerGoals: latestCareerGoals,
    ...overrides
  };
}

export const baseInternalUser: IUserProfile = {
  id: 'dev-user-1',
  name: 'Maya Mercer',
  email: devBypassAuthUser.email,
  email2: 'maya.alt@besthuman.test',
  type: UserRoleType.Mentee,
  clientId: 'client-dev-1',
  avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=150&h=150&q=80',
  linkedin: 'https://www.linkedin.com/in/maya-mercer-preview/',
  title: 'Director of Product',
  company: 'Northwind Labs',
  city: 'Vancouver',
  country: 'Canada',
  timezone: 'America/Vancouver',
  status: UserStatus.Active,
  careerGoals: latestCareerGoals
};

export const leaderLeoProfile: IUserProfile = {
  id: 'dev-leader-1',
  name: 'Jordan Lee',
  email: 'jordan.lee@besthuman.test',
  type: UserRoleType.Mentee,
  clientId: 'client-dev-1',
  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&h=150&q=80',
  linkedin: 'https://www.linkedin.com/in/jordan-lee-preview/',
  title: 'VP of People',
  company: 'Northwind Labs',
  city: 'Vancouver',
  country: 'Canada',
  timezone: 'America/Vancouver',
  status: UserStatus.Active,
  careerGoals: jordanCareerGoals
};

export const ceoEvanProfile: IUserProfile = {
  id: 'dev-ceo-evan',
  name: 'Evan Whitaker',
  email: 'evan.whitaker@besthuman.test',
  type: UserRoleType.Mentee,
  clientId: 'client-dev-1',
  avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=150&h=150&q=80',
  linkedin: 'https://www.linkedin.com/in/evan-whitaker-preview/',
  title: 'CEO & Co-Founder',
  company: 'Northstar Systems',
  city: 'Seattle',
  country: 'United States',
  timezone: 'America/Los_Angeles',
  status: UserStatus.Active,
  careerGoals: ceoCareerGoals
};

export const coachCaseyProfile: IUserProfile = {
  id: 'dev-co-leader-1',
  name: 'Casey Carter',
  email: 'casey.carter@besthuman.test',
  type: UserRoleType.Mentor,
  clientId: 'client-dev-1',
  avatar: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=150&h=150&q=80',
  linkedin: 'https://www.linkedin.com/in/casey-carter-preview/',
  title: 'Leadership Coach',
  company: 'BestHuman',
  city: 'Vancouver',
  country: 'Canada',
  timezone: 'America/Vancouver',
  status: UserStatus.Active,
  careerGoals: [
    {
      date: getDateOffset(-45, 9),
      goal: 'Help cohorts build practical leadership habits that stick.'
    }
  ]
};

export const newNoahProfile: IUserProfile = {
  id: 'dev-new-noah',
  name: 'Noah Nelson',
  email: 'noah.nelson@besthuman.test',
  type: UserRoleType.Mentee,
  clientId: 'client-dev-1',
  avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=150&h=150&q=80',
  linkedin: 'https://www.linkedin.com/in/noah-nelson-preview/',
  title: 'Junior Designer',
  company: 'Northwind Labs',
  city: 'Toronto',
  country: 'Canada',
  timezone: 'America/Toronto',
  status: UserStatus.New,
  careerGoals: []
};

export const veteranValProfile: IUserProfile = {
  id: 'dev-veteran-val',
  name: 'Val Vance',
  email: 'val.vance@besthuman.test',
  type: UserRoleType.Mentee,
  clientId: 'client-dev-1',
  avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=150&h=150&q=80',
  linkedin: 'https://www.linkedin.com/in/val-vance-preview/',
  title: 'Senior Portfolio Manager',
  company: 'Global Finance Corp',
  city: 'New York',
  country: 'United States',
  timezone: 'America/New_York',
  status: UserStatus.Active,
  careerGoals: [
    {
      date: getDateOffset(-365, 9),
      goal: 'Transition into strategic executive leadership.'
    }
  ]
};
